/**
 * Which card an answer belongs to (owner, 2026-10-03: "once the AI answer
 * arrives it should be 100% matched rightly to the right card").
 *
 * The AI server (`production_final_server.py` `_emit_detection`) sends three
 * frames per card, in order: `question_detected`, `stored_answer_generated`,
 * `answer_generated`. They share ids the card already has, so no new id is
 * needed:
 *
 * - `sourceId`: the knowledge-base entry (null for a New Inquiry);
 * - `timestamp`: the raising line's stamp, the same on all three frames;
 * - `participantId`: the customer who raised it (absent when the line named nobody);
 * - `question`: the card text (the key of a New Inquiry, which has no `sourceId`).
 *
 * An objection or FAQ pairs on sourceId + timestamp + participantId; a New
 * Inquiry on timestamp + participantId + question. A frame without
 * `timestamp` or without a `sourceId` field (an old server during a rolling
 * deploy) has no key, and the reducers fall back to today's text matching.
 * A keyed answer never goes to a card whose key differs: with no such card it
 * is logged and dropped. The desktop apps implement the same rule
 * (`CardAnswerPairing` in both); keep the three in step.
 */
import { parseServerTimestampMs } from './serverTimestamp';

export interface CardKey {
  /** Knowledge-base id; null for a New Inquiry. */
  sourceId: string | null;
  /** The raising line's stamp, as the server sent it. */
  timestamp: string;
  /** The raiser; null when the frame names nobody. */
  participantId: string | null;
  /** Compared only when there is no sourceId (a New Inquiry). */
  question: string;
}

type WireRecord = Record<string, unknown>;

/** Ids come as strings or (Recall participant ids) numbers; 0 is an id. */
const toId = (value: unknown): string | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return typeof value === 'string' && value !== '' ? value : null;
};

const asRecord = (value: unknown): WireRecord =>
  value && typeof value === 'object' ? (value as WireRecord) : {};

/** The key a detection frame (or a saved card row) carries, or null when it has none. */
export const cardKeyOf = (frame: unknown): CardKey | null => {
  const record = asRecord(frame);
  const timestamp = record.timestamp;
  if (typeof timestamp !== 'string' || timestamp === '' || !('sourceId' in record)) return null;
  return {
    sourceId: toId(record.sourceId),
    timestamp,
    participantId: toId(record.participantId),
    question: typeof record.question === 'string' ? record.question : '',
  };
};

/**
 * The same instant, to the millisecond. The live frames carry one identical
 * string; a card restored from the saved session holds the Mongo copy
 * (milliseconds, `Z`), which parses to the same millisecond.
 */
const isSameInstant = (first: string, second: string): boolean => {
  const firstMs = parseServerTimestampMs(first);
  const secondMs = parseServerTimestampMs(second);
  return firstMs !== null && secondMs !== null ? firstMs === secondMs : first === second;
};

export const isSameCard = (card: CardKey, frame: CardKey): boolean =>
  card.sourceId === frame.sourceId &&
  card.participantId === frame.participantId &&
  isSameInstant(card.timestamp, frame.timestamp) &&
  (card.sourceId !== null || card.question === frame.question);

/** For logs: the key in one line. */
export const describeCardKey = (key: CardKey): string =>
  `sourceId=${key.sourceId ?? 'none'} timestamp=${key.timestamp} participantId=${key.participantId ?? 'none'}` +
  (key.sourceId === null ? ` question="${key.question.slice(0, 60)}"` : '');

/** What an `edit_question` request names: the card being edited, by its own key. */
export interface EditQuestionRequest {
  originalQuestion: string;
  editedQuestion: string;
  cardKey: CardKey | null;
}

/**
 * The card keys an `edit_question` request carries, flat in `data` as the
 * server reads them (`EDIT_CARD_KEYS` in production_final_server.py): the
 * card's own line `timestamp` (not the edit time), and its `sourceId` and
 * `participantId` when it has them. The server echoes them on its reply and
 * the saver finds the edited row by them.
 */
export const editCardFields = (key: CardKey | null, cardTimestamp: string): WireRecord => ({
  timestamp: key?.timestamp ?? cardTimestamp,
  ...(key?.sourceId != null ? { sourceId: key.sourceId } : {}),
  ...(key?.participantId != null ? { participantId: key.participantId } : {}),
});

/**
 * Whether an `edit_question_response` answers this request. The reply goes
 * only to the socket that asked. When it echoes the card's keys (a server
 * since 2026-10-03 echoes `timestamp`, `sourceId`, `participantId` flat),
 * they must match the card's; otherwise (an older server, or a New Inquiry,
 * whose null sourceId is not echoed) the reply must name the request's own
 * original and edited text, so a late reply to an earlier request never
 * fills the dialog of a later one.
 */
export const isResponseToEdit = (response: unknown, request: EditQuestionRequest): boolean => {
  const record = asRecord(response);
  const echoed = cardKeyOf({ ...record, question: request.originalQuestion });
  if (echoed && request.cardKey) return isSameCard(request.cardKey, echoed);
  return record.original_question === request.originalQuestion && record.edited_question === request.editedQuestion;
};
